import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Tooltip,
  Button,
  Avatar,
  Image,
  DatePicker,
  Dropdown,
  Badge,
  Select,
} from "antd";
import { AiFillDelete } from "react-icons/ai";
import { RiAddBoxFill, RiFileListLine, RiInboxArchiveLine, RiLayoutGridFill } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  FileOutlined,
  FilePdfOutlined,
  MessageOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useRef, useState } from "react";
import {
  FaBriefcase,
  FaCheckCircle,
  FaEdit,
  FaFilePowerpoint,
  FaFileWord,
  FaFlag,
  FaHistory,
  FaHourglassHalf,
  FaPauseCircle,
  FaPen,
  FaRegCheckCircle,
  FaTrashAlt,
} from "react-icons/fa";
import React from "react";
import {
  deleteDocument,
  deleteProject,
  archiveProject,
  restoreProject,
  deleteProjectSummary,
  getallProject,
  getProjectByID,
  updateProject,
  getAllArchivedProject,
  getProjectStats,
} from "../../utils/API";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";
import { hasPermission } from "../../utils/HasPermission";
import AddProjectModal from "./Add";
import EditProjectModal from "./Edit";
import dayjs, { Dayjs } from "dayjs";
import { AuthContext } from "../../Auth/AuthContext";
import { MdOutlineRestore } from "react-icons/md";
import { FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import { HiOutlineDocumentText } from "react-icons/hi";
import { BsCheck2Circle } from "react-icons/bs";

// Define the Developer interface based on API response
interface Project {
  _id: string;
  clientId: { name: string; profilePhoto?: string };
  assignees: any[];
  projectName: string;
  budget: number;
  review: string;
  endingDate: string | Date | null;
  completionDate: string | Date;
  startDate: string | Date;
  isActive: boolean;
  isonhold?: boolean;
  iscompleted?: boolean;
  purposal?: string[];
  contract?: string[];
}

interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  onHoldProjects: number;
  completedProjects: number;
  totalTasks: number;
}

export const ProjectsTable = () => {
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/customers/";
  const PHOTOURL = process.env.REACT_APP_IMAGE_URL + "/developers/";

  const [activeTab, setActiveTab] = useState<
    "all" | "active" | "onhold" | "completed" | "archived"
  >("all");

  const [stats, setStats] = useState<ProjectStats>({
    totalProjects: 0,
    activeProjects: 0,
    onHoldProjects: 0,
    completedProjects: 0,
    totalTasks: 0,
  });

  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [archiveRecord, setArchiveRecord] = useState<Project | null>(null);
  const [restoreId, setRestoreId] = useState<string | null>(null);

  const [dataSource, setDataSource] = useState<Project[]>([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState<Project | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDocModalVisible, setIsDocModalVisible] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<{
    purposal?: string[];
    contract?: string[];
  }>({});

  const today = dayjs();
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [currentSummaryprojectId, setCurrentSummaryprojectId] = useState<
    string | null
  >(null);
  const [newSummary, setNewSummary] = useState<{
    date: Dayjs | null;
    description: string;
    nextActionDate: Dayjs | null;
  }>({
    date: dayjs(),
    description: "",
    nextActionDate: dayjs(),
  });
  const { authData } = useContext(AuthContext);
  const user_id = authData.user._id;

  const handleViewSummary = async (projectId: string) => {
    setCurrentSummaryprojectId(projectId);
    setIsSummaryModalOpen(true);
    setSummaryLoading(true);

    try {
      const res = await getProjectByID(projectId);
      setSummaryData(res?.summaries || []);
    } catch (err) {
      console.error("Error fetching summary:", err);
      setSummaryData([]);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await getProjectStats();
      if (res?.success) {
        setStats(res.stats);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response: any = await getallProject(searchText, page, activeTab, pageSize);

      if (response?.data) {
        setDataSource(response.data);
        setTotalRecords(response.totalCount || 0);
      } else {
        setDataSource([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchProject();
  }, [page, searchText, activeTab, pageSize]);

  const handleEditClick = (record: Project) => {
    setCurrentTask(record);
    setIsEditModalVisible(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        if (activeTab === "archived") {
          await deleteProject(deleteId);
          message.success("Project permanently deleted");
        } else {
          await archiveProject(deleteId);
          message.success("Project moved to archived");
        }
        fetchProject();
        fetchStats();
      } catch (error) {
        console.error("Delete Error:", error);
        message.error(
          activeTab === "archived"
            ? "Error permanently deleting project."
            : "Error archiving project.",
        );
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handleRestore = async () => {
    if (restoreId) {
      try {
        await restoreProject(restoreId);
        fetchProject();
        fetchStats();
        message.success("Project restored successfully");
      } catch (error) {
        console.error("Restore Error:", error);
        message.error("Error restoring project.");
      } finally {
        setRestoreId(null);
      }
    }
  };

  const handleStatusChange = async (
    projectId: string,
    nextStatus: "active" | "onhold" | "completed",
  ) => {
    try {
      const endingDate =
        nextStatus === "completed" ? dayjs().toISOString() : null;
      const data = {
        isActive: nextStatus === "active",
        isonhold: nextStatus === "onhold",
        iscompleted: nextStatus === "completed",
        endingDate,
      };
      await updateProject(projectId, data, user_id);
      message.success("Project status updated successfully!");
      fetchStats();
      fetchProject();
      setDataSource((prev) =>
        prev.map((project) =>
          project._id === projectId ? { ...project, ...data } : project,
        ),
      );
    } catch (error) {
      console.error("Error updating project status:", error);
      message.error("Failed to update status.");
    }
  };

  const StatusBadge = ({ record }: { record: Project }) => {
    let status = "Active";
    let statusKey: "active" | "onhold" | "completed" = "active";
    let color = "text-emerald-600 bg-white border-emerald-100 cursor-pointer hover:bg-emerald-50";
    let dotColor = "bg-emerald-500";

    if (
      activeTab !== "archived" &&
      (
        record.iscompleted === true ||
        (record.iscompleted === undefined && Boolean(record.endingDate))
      )
    ) {
      status = "Completed";
      statusKey = "completed";
      color = "text-purple-600 bg-white border-purple-100 cursor-pointer hover:bg-purple-50";
      dotColor = "bg-purple-500";
    } else if (
      activeTab === "archived" ||
      record.isonhold === true ||
      (record.isonhold === undefined && !record.isActive)
    ) {
      status = "On Hold";
      statusKey = "onhold";
      color = "text-orange-600 bg-white border-orange-100 cursor-pointer hover:bg-orange-50";
      dotColor = "bg-orange-500";
    }

    return (
      <Dropdown
        trigger={["click"]}
        disabled={!hasPermission("projects", "update")}
        menu={{
          selectedKeys: [statusKey],
          items: [
            {
              key: "active",
              label: (
                <span className="flex items-center gap-2 text-[12px] font-semibold text-emerald-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
              ),
            },
            {
              key: "onhold",
              label: (
                <span className="flex items-center gap-2 text-[12px] font-semibold text-orange-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                  On Hold
                </span>
              ),
            },
            {
              key: "completed",
              label: (
                <span className="flex items-center gap-2 text-[12px] font-semibold text-purple-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                  Completed
                </span>
              ),
            },
          ],
          onClick: ({ key }) => {
            if (key !== statusKey) {
              handleStatusChange(
                record._id,
                key as "active" | "onhold" | "completed",
              );
            }
          },
        }}
      >
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold shadow-sm transition-all ${color}`}
        >
          <span className={`h-1 w-1 rounded-full ${dotColor}`} />
          {status}
          <DownOutlined className="ml-0.5 text-[8px] opacity-70" />
        </button>
      </Dropdown>
    );
  };

  const compareText = (first?: string, second?: string) =>
    (first || "").localeCompare(second || "", undefined, {
      sensitivity: "base",
    });

  const compareDates = (first?: string | Date | null, second?: string | Date | null) => {
    const firstDate = first ? dayjs(first).valueOf() : 0;
    const secondDate = second ? dayjs(second).valueOf() : 0;

    return firstDate - secondDate;
  };

  const getAssigneeName = (user: any) =>
    user?.username || user?.name || "N/A";

  const getAssigneeInitial = (user: any) =>
    getAssigneeName(user).charAt(0).toUpperCase();

  const getStatusSortValue = (project: Project) => {
    if (
      project.iscompleted === true ||
      (project.iscompleted === undefined && Boolean(project.endingDate))
    ) return 3;
    if (
      project.isonhold === true ||
      (project.isonhold === undefined && !project.isActive)
    ) return 2;
    return 1;
  };

  const columns = [
    {
      title: "#",
      key: "serialNumber",
      width: 60,
      fixed: "left" as const,
      render: (_: any, __: any, index: number) => (
        <span className="text-slate-800 font-semibold text-[15px]">{(page - 1) * pageSize + (index + 1)}</span>
      ),
    },
    {
      title: (
        <span>Client Name</span>
      ),
      dataIndex: "clientId",
      width: 220,
      fixed: "left" as const,
      sorter: (first: Project, second: Project) =>
        compareText(first.clientId?.name, second.clientId?.name),
      render: (client: any) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={client?.profilePhoto ? `${IMAGEURL}${client.profilePhoto}` : "/download.png"}
            size={32}
            className="border border-slate-100 shadow-sm shrink-0"
          >
            {client?.name?.charAt(0)}
          </Avatar>
          <span className="text-slate-700 font-bold text-[13px] truncate">
            {client?.name || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: (
        <span>Assignees</span>
      ),
      dataIndex: "assignees",
      width: 160,
      fixed: "left" as const,
      sorter: (first: Project, second: Project) =>
        compareText(
          first.assignees?.map(getAssigneeName).join(", "),
          second.assignees?.map(getAssigneeName).join(", "),
        ),
      render: (assignees: any[]) => {
        if (!assignees || assignees.length === 0) return "-";
        const visible = assignees.slice(0, 2);
        const hiddenAssignees = assignees.slice(2);
        const extra = hiddenAssignees.length;
        return (
          <Dropdown
            trigger={["click"]}
            placement="bottomLeft"
            disabled={hiddenAssignees.length === 0}
            dropdownRender={() => (
              <div className="max-h-64 min-w-[190px] overflow-y-auto rounded-lg bg-white p-2 shadow-lg">
                {hiddenAssignees.map((user) => (
                  <div
                    key={user._id}
                    className="flex items-center gap-2 rounded-md px-3 py-2"
                  >
                    <Avatar
                      src={user.photo ? `${PHOTOURL}${user.photo}` : undefined}
                      size={26}
                      className="shrink-0 bg-slate-300 text-slate-700"
                    >
                      {getAssigneeInitial(user)}
                    </Avatar>
                    <span className="text-[13px] font-medium text-slate-700">
                      {user.username || user.name || "N/A"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          >
            <button
              type="button"
              aria-label="View project assignees"
              className="flex cursor-pointer items-center border-0 bg-transparent p-0"
            >
              <div className="flex items-center -space-x-1">
                {visible.map((user) => (
                  <Tooltip key={user._id} title={user.username}>
                    <Avatar
                      src={user.photo ? `${PHOTOURL}${user.photo}` : undefined}
                      size={38}
                      className="border-2 border-white bg-slate-300 text-slate-700 shadow-sm shrink-0"
                    >
                      {getAssigneeInitial(user)}
                    </Avatar>
                  </Tooltip>
                ))}
                {extra > 0 && (
                  <div className="z-10 flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border-2 border-white bg-slate-100 text-[10px] font-bold text-slate-600 shadow-sm">
                    +{extra}
                  </div>
                )}
              </div>
            </button>
          </Dropdown>
        );
      },
    },
    {
      title: (
        <span>Project Name</span>
      ),
      dataIndex: "projectName",
      width: 240,
      sorter: (first: Project, second: Project) =>
        compareText(first.projectName, second.projectName),
      render: (text: string) => {
        const projectName = text || "N/A";
        const showTooltip = projectName.length > 24;

        return (
        <div className="flex min-w-0 flex-col">
          <Tooltip title={showTooltip ? projectName : ""}>
            <span className="block max-w-[210px] truncate text-[13px] font-bold leading-tight text-slate-800">
              {projectName}
            </span>
          </Tooltip>
          <span className="text-slate-400 text-[11px] mt-0.5 font-medium">
            Internal Project
          </span>
        </div>
        );
      },
    },
    {
      title: (
        <span>Start Date</span>
      ),
      dataIndex: "startDate",
      width: 140,
      sorter: (first: Project, second: Project) =>
        compareDates(first.startDate, second.startDate),
      render: (text: any) => (
        <div className="flex items-center gap-1.5 text-slate-500 text-[13px] font-bold">
          <CalendarOutlined className="text-slate-400 text-xs" />
          {text ? dayjs(text).format("DD MMM YYYY") : "-"}
        </div>
      ),
    },
    {
      title: (
        <span>Target Date</span>
      ),
      dataIndex: "completionDate",
      width: 160,
      sorter: (first: Project, second: Project) =>
        compareDates(first.completionDate, second.completionDate),
      render: (text: any) => (
        <div className="flex items-center gap-1.5 text-slate-500 text-[13px] font-bold">
          <CalendarOutlined className="text-slate-400 text-xs" />
          {text ? dayjs(text).format("DD MMM YYYY") : "-"}
        </div>
      ),
    },
    {
      title: (
        <span>Complete Date</span>
      ),
      dataIndex: "endingDate",
      width: 180,
      sorter: (first: Project, second: Project) =>
        compareDates(first.endingDate, second.endingDate),
      render: (text: any) => (
        <div className="flex items-center gap-1.5 text-slate-500 text-[13px] font-bold">
          {text ? (
            <>
              <CalendarOutlined className="text-slate-400 text-xs" />
              {dayjs(text).format("DD MMM YYYY")}
              <FaCheckCircle className="text-emerald-500 ml-0.5" />
            </>
          ) : (
            <span className="ml-5 text-slate-300">—</span>
          )}
        </div>
      ),
    },
    {
      title: "Summary",
      key: "summary",
      width: 110,
      align: "center" as const,
      render: (_: any, record: any) => (
        <div className="flex justify-center">
          <button
            onClick={() => handleViewSummary(record._id)}
            className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-600 shadow-sm transition-colors hover:bg-emerald-100"
          >
            <RiLayoutGridFill size={17} />
          </button>
        </div>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 120,
      align: "center" as const,
      render: (_: any, record: any) => {
        const count = (record.purposal?.length || 0) + (record.contract?.length || 0);
        return (
          <div className="flex justify-center">
            <div className="relative inline-block">
            <button
              onClick={() => {
                setCurrentTask(record);
                setSelectedDocs({
                  purposal: record.purposal || [],
                  contract: record.contract || [],
                });
                setIsDocModalVisible(true);
              }}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:border-slate-400 hover:text-slate-700"
            >
              <HiOutlineDocumentText size={18} />
            </button>
            {count > 0 && (
              <span className="absolute -right-2.5 -top-2.5 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-bold leading-none text-slate-700 shadow-sm">
                {count}
              </span>
            )}
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span>Status</span>
      ),
      dataIndex: "isActive",
      width: 140,
      sorter: (first: Project, second: Project) =>
        getStatusSortValue(first) - getStatusSortValue(second),
      render: (_: any, record: Project) => <StatusBadge record={record} />,
    },
    {
      title: "Action",
      key: "action",
      width: 130,
      align: "center" as const,
      render: (_: any, record: Project) => (
        <div className="flex items-center justify-center gap-1">
          <Tooltip title="Edit">
            <button
              onClick={() => handleEditClick(record)}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-100 bg-white text-slate-500 shadow-sm transition-all hover:border-slate-300 hover:text-slate-700"
            >
              <FaPen size={13} />
            </button>
          </Tooltip>
          <Tooltip title={activeTab === "archived" ? "Delete" : "Archive"}>
            <button
              onClick={() => setDeleteId(record._id)}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-100 bg-white text-rose-500 shadow-sm transition-all hover:border-rose-200 hover:bg-rose-50"
            >
              <FaTrashAlt size={13} />
            </button>
          </Tooltip>
          {activeTab === "archived" && (
            <Tooltip title="Restore">
              <button
                onClick={() => setRestoreId(record._id)}
                className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-slate-100 bg-white text-emerald-600 shadow-sm transition-all hover:border-emerald-200 hover:bg-emerald-50"
              >
                <MdOutlineRestore size={17} />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  const handleDeletedocument = async (doc: string, projectId: string) => {
    try {
      await deleteDocument(projectId, doc);
      message.success("Document deleted successfully");
      fetchProject();
      setSelectedDocs((prev) => ({
        purposal: prev.purposal?.filter((d) => d !== doc),
        contract: prev.contract?.filter((d) => d !== doc),
      }));
    } catch (error: any) {
      console.error(error);
      message.error("Failed to delete document");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] px-3 py-4 sm:px-4 lg:px-6 lg:py-5">
      <style>{`
        .custom-table-wrapper {
          border: 1px solid #f1f5f9 !important;
          border-radius: 16px !important;
          background-color: #fff !important;
          overflow: hidden !important;
        }
        .ant-table-thead > tr > th {
          background: #f1f5f9 !important;
          color: #374151 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          text-transform: none !important;
          letter-spacing: 0 !important;
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 20px 12px !important;
          white-space: nowrap !important;
          vertical-align: top !important;
        }
        .ant-table-thead > tr > th:first-child {
          text-align: left !important;
          vertical-align: top !important;
          padding-left: 24px !important;
          border-top-left-radius: 16px !important;
        }
        .ant-table-thead > tr > th:last-child {
          border-top-right-radius: 16px !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 14px 12px !important;
          vertical-align: middle !important;
        }
        .ant-table-tbody > tr > td:first-child {
          padding-left: 24px !important;
        }

        .ant-table {
          background: #fff !important;
        }
        .ant-pagination-item-active {
          background-color: #0f172a !important;
          border-color: #0f172a !important;
        }
        .ant-pagination-item-active a {
          color: white !important;
        }
        .custom-project-table .ant-table-container {
          border-radius: 16px 16px 0 0 !important;
          overflow: hidden !important;
        }
        .custom-project-table .ant-table-body,
        .custom-project-table .ant-table-content {
          overflow-x: auto !important;
          scrollbar-color: #dfe4ea transparent;
          scrollbar-width: thin;
        }
        .custom-project-table .ant-table-body::-webkit-scrollbar,
        .custom-project-table .ant-table-content::-webkit-scrollbar {
          height: 5px;
        }
        .custom-project-table .ant-table-body::-webkit-scrollbar-track,
        .custom-project-table .ant-table-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-project-table .ant-table-body::-webkit-scrollbar-thumb,
        .custom-project-table .ant-table-content::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: #dfe4ea;
        }
        .project-page-size-select .ant-select-selector {
          height: 36px !important;
          border-radius: 8px !important;
          border-color: #e2e8f0 !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05) !important;
        }
        .project-page-size-select .ant-select-selection-item {
          display: flex !important;
          align-items: center !important;
          color: #475569 !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          line-height: 34px !important;
        }
        .project-dialog .ant-modal-content {
          display: flex;
          max-height: calc(100vh - 32px);
          flex-direction: column;
          overflow: hidden;
          border-radius: 12px;
          padding: 0;
        }
        .project-dialog .ant-modal-header {
          margin: 0;
          border-bottom: 1px solid #edf1f5;
          padding: 15px 18px 13px;
        }
        .project-dialog .ant-modal-body {
          max-height: calc(100vh - 96px);
          overflow-y: auto;
          padding: 16px 18px 18px;
        }
        .project-dialog .ant-modal-close {
          inset-inline-end: 11px;
          top: 10px;
          height: 32px;
          width: 32px;
        }
        .project-delete-dialog .ant-modal-content {
          max-height: calc(100vh - 32px);
          overflow: hidden;
          border-radius: 12px;
          padding: 0;
        }
        .project-delete-dialog .ant-modal-body {
          padding: 24px 24px 14px;
        }
        .project-delete-dialog .ant-modal-footer {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin: 0;
          padding: 0 24px 22px;
        }
        .project-delete-dialog .ant-modal-footer .ant-btn + .ant-btn {
          margin-inline-start: 0;
        }
        @media (max-width: 1024px) {
          .project-dialog,
          .project-delete-dialog {
            max-width: calc(100vw - 24px) !important;
          }
        }
      `}</style>

      {/* Header Section */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 xl:flex-row xl:items-center">
        <div className="flex items-start gap-3">
          <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-center shrink-0">
            <FaBriefcase className="text-emerald-600 text-xl" />
          </div>
          <div>
            <h1 className="text-[19px] font-black text-slate-900 m-0 tracking-tight">Projects</h1>
            <p className="text-slate-500 text-[12px] m-0 mt-0.5 font-medium opacity-70">
              Manage and track all your projects in one place
            </p>
          </div>
        </div>

        <div className="grid w-full gap-2 sm:grid-cols-[minmax(0,1fr)_auto] xl:w-auto">
          <div className="relative w-full sm:min-w-[280px] xl:w-80">
            <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 text-sm" />
            <Input
              placeholder="Search projects and clients"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 h-9 bg-white border-slate-200 rounded-lg hover:border-slate-300 focus:border-slate-400 shadow-sm text-[13px]"
            />
          </div>
          <Button
            type="primary"
            onClick={() => setIsModalOpen(true)}
            icon={<PlusOutlined style={{ fontSize: '12px' }} />}
            className="h-9 px-4 rounded-lg bg-slate-900 border-none flex items-center gap-1.5 hover:!bg-slate-900 shadow-md text-[13px] font-bold"
          >
            Add Project
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        {[
          {
            label: "Total Projects",
            value: stats.totalProjects,
            icon: <FaBriefcase size={14} className="text-emerald-600" />,
            bg: "bg-emerald-50",
            trend: "+ 12%",
            trendUp: true,
            color: "#10b981",
          },
          {
            label: "Active Projects",
            value: stats.activeProjects,
            icon: <BsCheck2Circle size={16} className="text-amber-500" />,
            bg: "bg-amber-50",
            trend: "+ 8%",
            trendUp: true,
            color: "#f59e0b",
          },
          {
            label: "On Hold",
            value: stats.onHoldProjects,
            icon: <FaHourglassHalf size={14} className="text-orange-500" />,
            bg: "bg-orange-50",
            trend: "- 3%",
            trendUp: false,
            color: "#f43f5e",
          },
          {
            label: "Completed",
            value: stats.completedProjects,
            icon: <FaFlag size={14} className="text-purple-500" />,
            bg: "bg-purple-50",
            trend: "0%",
            trendUp: null,
            color: "#8b5cf6",
          },
          {
            label: "Total Tasks",
            value: stats.totalTasks,
            icon: <RiFileListLine size={16} className="text-slate-600" />,
            bg: "bg-slate-50",
            trend: "+ 15%",
            trendUp: true,
            color: "#64748b",
          },
        ].map((item, i) => (
          <Card
            key={i}
            className="min-w-0 overflow-hidden rounded-2xl border border-slate-100 shadow-sm"
            bodyStyle={{ padding: '18px 12px' }}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className={`${item.bg} w-10 h-10 rounded-xl flex items-center justify-center shadow-inner shrink-0`}>
                  {item.icon}
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-900 text-xl font-black leading-none mb-1">
                    {item.value}
                  </span>
                  <span className="text-[10px] font-bold leading-tight text-slate-500 opacity-60">
                    {item.label.split(" ").map((word, index, words) => (
                      <React.Fragment key={`${word}-${index}`}>
                        <span className="block xl:inline">{word}</span>
                        {index < words.length - 1 && (
                          <span className="hidden xl:inline"> </span>
                        )}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div className="h-6 w-14 mb-1">
                   <svg width="100%" height="20" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <path
                      d="M5,26 L12,26 C17,26 21,14 25,14 C28,14 29,17 32,17 C35,17 36,10 38,10 C41,10 43,16 45,16 C48,16 50,15 52,15 C56,15 59,24 62,24 C66,24 68,12 72,12 C76,12 78,25 80,25 C83,25 85,18 88,18"
                      fill="none"
                      stroke={item.trendUp === null ? "#94a3b8" : item.color}
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                   </svg>
                </div>
                <span className={`text-[9px] font-black flex items-center gap-0.5 ${
                  item.trendUp === null ? "text-slate-400" : ""
                }`} style={{ color: item.trendUp !== null ? item.color : undefined }}>
                  {item.trendUp === true ? <FiTrendingUp size={10} /> : item.trendUp === false ? <FiTrendingDown size={10} /> : null}
                  {item.trend}
                </span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Tabs Section */}
      <div className="flex items-center gap-8 mb-0 border-b border-slate-100 px-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
        {[
          { key: "all", label: "All Projects" },
          { key: "active", label: "Active" },
          { key: "onhold", label: "On Hold" },
          { key: "completed", label: "Completed" },
          { key: "archived", label: "Archived" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key as any);
              setPage(1);
            }}
            className={`pb-3 text-[13px] font-extrabold transition-all relative outline-none ${
              activeTab === tab.key
                ? "text-slate-900"
                : "text-slate-400 hover:text-slate-500"
            }`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Table + Footer Section */}
      <div className="custom-table-wrapper mt-3 shadow-sm">
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="_id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1645 }}
          className="custom-project-table"
        />
        {/* Footer / Pagination */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row">
          <div className="text-center text-[11px] font-medium text-slate-500 sm:text-left">
            Showing <span className="text-slate-700">{totalRecords ? (page - 1) * pageSize + 1 : 0}</span> to{" "}
            <span className="text-slate-700">{Math.min(page * pageSize, totalRecords)}</span> of{" "}
            <span className="text-slate-700">{totalRecords}</span> projects
          </div>
          <div className="flex w-full flex-wrap items-center justify-center gap-2 sm:w-auto sm:justify-end">
            <Pagination
              current={page}
              total={totalRecords}
              pageSize={pageSize}
              onChange={setPage}
            />
            <Select
              value={pageSize.toString()}
              onChange={(nextPageSize) => {
                setPageSize(Number(nextPageSize));
                setPage(1);
              }}
              options={["10", "20", "40", "50", "100"].map((size) => ({
                value: size,
                label: `${size} / page`,
              }))}
              className="project-page-size-select ml-2 w-[104px]"
            />
          </div>
        </div>
      </div>

      {/* All Modals */}
      <AddProjectModal
        visible={isModalOpen}
        onTaskCreated={() => {
          setIsModalOpen(false);
          fetchProject();
          fetchStats();
        }}
        onClose={() => setIsModalOpen(false)}
      />

      {currentTask && (
        <EditProjectModal
          visible={isEditModalVisible}
          taskId={currentTask._id}
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchProject();
            fetchStats();
          }}
        />
      )}

      {/* Summary Modal */}
      <Modal
        title={
          <span className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
            <RiLayoutGridFill className="text-emerald-600" />
            Project Summary
          </span>
        }
        centered
        open={isSummaryModalOpen}
        onCancel={() => setIsSummaryModalOpen(false)}
        footer={null}
        width={680}
        destroyOnClose
        className="project-dialog project-summary-dialog"
      >
        <Spin spinning={summaryLoading}>
          <div className="max-h-[230px] overflow-y-auto pr-1">
            {summaryData.length > 0 ? (
              <div className="space-y-2.5">
                {summaryData.map((summary, idx) => (
                  <div key={idx} className="group relative rounded-[8px] border border-slate-200 bg-slate-50 px-3 py-3">
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-[6px] border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 shadow-sm">
                          {summary.date}
                        </span>
                        {summary.nextActionDate && (
                          <span className="rounded-[6px] border border-amber-100 bg-amber-50 px-2 py-1 text-[10px] font-semibold uppercase text-amber-600">
                            Next: {summary.nextActionDate}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            setSummaryLoading(true);
                            await deleteProjectSummary(currentSummaryprojectId, summary._id);
                            message.success("Summary deleted");
                            const res = await getProjectByID(currentSummaryprojectId || "");
                            setSummaryData(res?.summaries || []);
                            fetchProject();
                          } catch (err) {
                            message.error("Failed to delete summary");
                          } finally {
                            setSummaryLoading(false);
                          }
                        }}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border border-slate-200 bg-white text-slate-400 transition-colors hover:border-rose-200 hover:text-rose-500"
                        aria-label="Delete summary"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                    <p className="m-0 text-[13px] leading-relaxed text-slate-700">
                      {summary.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-[8px] border border-dashed border-slate-200 py-8 text-center text-[13px] text-slate-400">
                No summaries added yet
              </div>
            )}
          </div>

          <div className="mt-4 border-t border-slate-100 pt-4">
             <h4 className="mb-3 text-[14px] font-semibold text-slate-900">Add New Summary</h4>
             <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DatePicker
                  placeholder="Summary Date"
                  value={newSummary.date}
                  onChange={(val) => setNewSummary({ ...newSummary, date: val })}
                  className="h-9 w-full rounded-[7px] border-slate-200"
                />
                <DatePicker
                  placeholder="Next Action Date"
                  value={newSummary.nextActionDate}
                  onChange={(val) => setNewSummary({ ...newSummary, nextActionDate: val })}
                  className="h-9 w-full rounded-[7px] border-slate-200"
                />
             </div>
             <Input.TextArea
                rows={3}
                placeholder="Describe the latest project status..."
                value={newSummary.description}
                onChange={(e) => setNewSummary({ ...newSummary, description: e.target.value })}
                className="mb-3 rounded-[7px] border-slate-200"
             />
             <div className="flex justify-end gap-2">
                <Button onClick={() => setIsSummaryModalOpen(false)} className="h-9 rounded-[7px] border-slate-200 px-5 text-[13px] font-semibold">
                  Cancel
                </Button>
                <Button
                  type="primary"
                  className="h-9 rounded-[7px] border-none bg-slate-900 px-5 text-[13px] font-semibold hover:!bg-slate-900"
                  onClick={async () => {
                    if (!newSummary.description?.trim()) return message.error("Please enter a description");
                    try {
                      setSummaryLoading(true);
                      const formData = new FormData();
                      formData.append("newSummary", JSON.stringify({
                        description: newSummary.description,
                        date: newSummary.date?.format("DD MMM YYYY"),
                        nextActionDate: newSummary.nextActionDate?.format("DD MMM YYYY"),
                      }));
                      await updateProject(currentSummaryprojectId || "", formData, user_id);
                      message.success("Summary added");
                      const res = await getProjectByID(currentSummaryprojectId || "");
                      setSummaryData(res?.summaries || []);
                      setNewSummary({ date: dayjs(), description: "", nextActionDate: dayjs() });
                      fetchProject();
                    } catch (err) {
                      message.error("Failed to add summary");
                    } finally {
                      setSummaryLoading(false);
                    }
                  }}
                >
                  Save Summary
                </Button>
             </div>
          </div>
        </Spin>
      </Modal>

      {/* Document Modal */}
      <Modal
        open={isDocModalVisible}
        onCancel={() => setIsDocModalVisible(false)}
        footer={null}
        width={680}
        title={
          <span className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
            <HiOutlineDocumentText className="text-slate-600" />
            Project Documents
          </span>
        }
        className="project-dialog project-documents-dialog"
      >
        <div className="flex flex-col gap-4">
          {((selectedDocs.purposal?.length || 0) + (selectedDocs.contract?.length || 0)) > 0 ? (
            <>
              {selectedDocs.purposal && selectedDocs.purposal.length > 0 && (
                <div>
                  <h3 className="mb-2 px-1 text-[12px] font-semibold uppercase text-slate-500">Proposals</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {selectedDocs.purposal.map((doc, i) => (
                      <div key={i} className="group relative cursor-pointer rounded-[8px] border border-slate-200 bg-slate-50 p-3 transition-colors hover:border-slate-300"
                        onClick={() => window.open(`${process.env.REACT_APP_IMAGE_URL}/projects/${doc}`, '_blank')}
                      >
                        <div className="flex items-center gap-3">
                          <div className="rounded-[6px] border border-slate-100 bg-white p-2 shadow-sm">
                            <FilePdfOutlined className="text-rose-500 text-lg" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-[13px] font-bold text-slate-700 truncate">{doc.split('-').pop()}</span>
                            <span className="text-[10px] font-medium text-slate-400">PDF Document</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeletedocument(doc, currentTask?._id || "") }}
                          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-rose-500 opacity-0 shadow-sm transition-all hover:bg-rose-50 group-hover:opacity-100"
                        >
                          <DeleteOutlined className="text-[10px]" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedDocs.contract && selectedDocs.contract.length > 0 && (
                <div>
                  <h3 className="mb-2 px-1 text-[12px] font-semibold uppercase text-slate-500">Contracts & NDAs</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {selectedDocs.contract.map((doc, i) => (
                      <div key={i} className="group relative cursor-pointer rounded-[8px] border border-slate-200 bg-slate-50 p-3 transition-colors hover:border-slate-300"
                        onClick={() => window.open(`${process.env.REACT_APP_IMAGE_URL}/projects/${doc}`, '_blank')}
                      >
                        <div className="flex items-center gap-3">
                          <div className="rounded-[6px] border border-slate-100 bg-white p-2 shadow-sm">
                            <FileOutlined className="text-blue-500 text-lg" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-[13px] font-bold text-slate-700 truncate">{doc.split('-').pop()}</span>
                            <span className="text-[10px] font-medium text-slate-400">Word/Doc</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeletedocument(doc, currentTask?._id || "") }}
                          className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-rose-500 opacity-0 shadow-sm transition-all hover:bg-rose-50 group-hover:opacity-100"
                        >
                          <DeleteOutlined className="text-[10px]" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 rounded-[8px] border border-dashed border-slate-200 py-9 text-slate-400">
              <FileOutlined className="text-3xl opacity-40" />
              <span className="text-[13px] font-medium">No documents uploaded for this project</span>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText={activeTab === "archived" ? "Delete Project" : "Archive Project"}
        cancelText="Cancel"
        centered
        width={420}
        okButtonProps={{ className: "h-9 rounded-[7px] border-none bg-rose-600 px-5 text-[13px] font-semibold hover:!bg-rose-600" }}
        cancelButtonProps={{ className: "h-9 rounded-[7px] border-slate-200 px-5 text-[13px] font-semibold" }}
        className="project-delete-dialog"
      >
        <div className="flex flex-col items-center py-1 text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-rose-50">
             {activeTab === "archived" ? (
               <DeleteOutlined className="text-[21px] text-rose-500" />
             ) : (
               <RiInboxArchiveLine className="text-[23px] text-rose-500" />
             )}
          </div>
          <h3 className="mb-1.5 text-[18px] font-semibold text-slate-900">
            {activeTab === "archived"
              ? "Delete this project?"
              : "Archive this project?"}
          </h3>
          <p className="m-0 max-w-[320px] text-[13px] leading-relaxed text-slate-500">
            {activeTab === "archived"
              ? "This action cannot be undone. This will permanently delete the project and all associated data."
              : "The project will move to Archived. You can restore it later."}
          </p>
        </div>
      </Modal>

      {/* Restore Confirmation */}
      <Modal
        open={!!restoreId}
        onOk={handleRestore}
        onCancel={() => setRestoreId(null)}
        okText="Restore Project"
        cancelText="Cancel"
        centered
        width={420}
        okButtonProps={{ className: "h-9 rounded-[7px] border-none bg-emerald-600 px-5 text-[13px] font-semibold hover:!bg-emerald-600" }}
        cancelButtonProps={{ className: "h-9 rounded-[7px] border-slate-200 px-5 text-[13px] font-semibold" }}
        className="project-delete-dialog"
      >
        <div className="flex flex-col items-center py-1 text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
            <MdOutlineRestore className="text-[24px] text-emerald-600" />
          </div>
          <h3 className="mb-1.5 text-[18px] font-semibold text-slate-900">
            Restore this project?
          </h3>
          <p className="m-0 max-w-[320px] text-[13px] leading-relaxed text-slate-500">
            The project will return to its previous status.
          </p>
        </div>
      </Modal>

    </div>
  );
};

const Pagination = ({ current, total, pageSize, onChange }: any) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-all hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
      </button>

      <button
        className="h-9 w-9 rounded-lg bg-slate-900 text-[13px] font-bold text-white shadow-lg"
      >
        {current}
      </button>

      <button
        onClick={() => current < totalPages && onChange(current + 1)}
        disabled={current === totalPages}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-all hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
      </button>
    </div>
  );
};

export const PROJECT_TABLE_COLUMNS = [
  "clientId",
  "assignees",
  "projectName",
  "startDate",
  "completionDate",
  "endingDate",
  "isActive",
];
// ============================================
